import { EMR_PAGE_SOURCE_LABELS, type EmrPageSource } from "@emr/contracts";
import type { IncomingDocumentsApi } from "../api/incomingDocuments";
import type { ExtractedFile } from "./extraction";

/** What filing the patient's files needs of the API client. */
export type ImportedFilesClient = Pick<IncomingDocumentsApi, "fileMigratedDocument">;

/**
 * Files the kept files on the registered patient's chart as migrated
 * documents, one at a time, each under the other EMR's name and with its
 * filing details. Every file is tried; when any could not be filed it says how
 * many, and a retry files the rest (those already filed are found, not filed
 * twice).
 */
export async function fileImportedFiles(
  client: ImportedFilesClient,
  patientId: string,
  source: EmrPageSource,
  files: readonly ExtractedFile[],
  onProgress?: (filed: number, total: number) => void,
): Promise<void> {
  let failed = 0;
  for (const [index, file] of files.entries()) {
    onProgress?.(index, files.length);
    try {
      await client.fileMigratedDocument(patientId, {
        bytes: file.bytes,
        fileName: file.fileName,
        mimeType: file.mimeType,
        source: {
          system: EMR_PAGE_SOURCE_LABELS[source],
          reference: file.reference,
          title: file.title,
          ...(file.categoryHint === undefined ? {} : { categoryHint: file.categoryHint }),
          ...(file.effectiveDate === undefined ? {} : { effectiveDate: file.effectiveDate }),
          ...(file.receivedAt === undefined ? {} : { receivedAt: file.receivedAt }),
          ...(file.reviewedAt === undefined ? {} : { reviewedAt: file.reviewedAt }),
          ...(file.notes === undefined ? {} : { notes: file.notes }),
        },
      });
    } catch {
      failed += 1;
    }
  }
  onProgress?.(files.length, files.length);
  if (failed > 0) {
    throw new Error(
      `${failed} of ${files.length} ${files.length === 1 ? "file" : "files"} could not be filed.`,
    );
  }
}
